package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.shared.domain.model.SearchCriteria
import com.islamicmedia.site.theme.*
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun SearchCriteriaPanel(
    criteria: SearchCriteria,
    onCriteriaChange: (SearchCriteria) -> Unit,
    expanded: Boolean,
    /**
     * V2: When Quick check is selected, custom concerns and child age ranges aren't processed
     * (Phase 1 is criteria-independent). Inputs are disabled with an explanatory banner.
     */
    quickCheck: Boolean = false
) {
    var showAgePicker by remember { mutableStateOf(false) }
    val inputsEnabled = !quickCheck

    // Slide-only animation with CSS transitions (no fade, part of unified card)
    Div(attrs = {
        style {
            width(100.percent)
            property("box-sizing", "border-box")
            property("overflow", "hidden")
            // Slide animation only - no opacity change
            property("max-height", if (expanded) "600px" else "0px")
            property("transition", "max-height 0.7s cubic-bezier(0.4, 0, 0.2, 1)")
        }
    }) {
        // Separator line
        Div(attrs = {
            style {
                height(1.px)
                property("background", "linear-gradient(90deg, transparent, rgba(14, 124, 123, 0.2), transparent)")
                marginBottom(16.px)
            }
        }) {}

        // Custom concerns textarea
        Div(attrs = {
            style {
                padding(0.px, 20.px, 20.px)
            }
        }) {
            Div(attrs = {
                style {
                    fontSize(14.px)
                    fontWeight(600)
                    color(if (inputsEnabled) Gray700 else Gray400)
                    marginBottom(10.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                }
            }) {
                Text("Additional concerns to check for")
            }

            TextArea(attrs = {
                value(criteria.customConcerns)
                onInput { onCriteriaChange(criteria.copy(customConcerns = it.value)) }
                placeholder("e.g., References to dating, scary scenes, death of a parent...")
                if (!inputsEnabled) attr("disabled", "")
                style {
                    width(100.percent)
                    property("min-height", "90px")
                    padding(16.px)
                    borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
                    border(1.px, LineStyle.Solid, Gray200)
                    fontSize(14.px)
                    property("font-family", "inherit")
                    property("resize", "vertical")
                    property("outline", "none")
                    property("box-sizing", "border-box")
                    property("transition", TransitionFast)
                    lineHeight(1.6.em)
                    if (!inputsEnabled) {
                        property("background", "#F5F5F5")
                        color(Gray400)
                        cursor("not-allowed")
                    }
                }
            })

            // Child ages section
            Div(attrs = {
                style {
                    marginTop(16.px)
                    fontSize(14.px)
                    fontWeight(600)
                    color(if (inputsEnabled) Gray700 else Gray400)
                    marginBottom(10.px)
                }
            }) {
                Text("Child age(s)")
            }

            // Chips + "Add age..." button in a flex-wrap row
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    property("flex-wrap", "wrap")
                    gap(8.px)
                    alignItems(AlignItems.Center)
                }
            }) {
                // Existing age range chips
                criteria.childAgeRanges.forEach { range ->
                    Div(attrs = {
                        style {
                            property("display", "inline-flex")
                            alignItems(AlignItems.Center)
                            gap(4.px)
                            padding(4.px, 4.px, 4.px, 12.px)
                            borderRadius(9999.px)
                            property("background", if (inputsEnabled) PrimaryPale.toString() else "#F5F5F5")
                            property("border", "1px solid " + if (inputsEnabled) "rgba(20, 163, 160, 0.3)" else "rgba(0,0,0,0.08)")
                            fontSize(14.px)
                            fontWeight(500)
                            color(if (inputsEnabled) Color("#065A5A") else Gray400)
                        }
                    }) {
                        Text(range)
                        // Remove button (only clickable when inputs enabled)
                        Span(attrs = {
                            if (inputsEnabled) {
                                onClick {
                                    onCriteriaChange(criteria.copy(childAgeRanges = criteria.childAgeRanges - range))
                                }
                            }
                            style {
                                width(20.px)
                                height(20.px)
                                borderRadius(50.percent)
                                property("display", "inline-flex")
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                cursor(if (inputsEnabled) "pointer" else "not-allowed")
                                fontSize(12.px)
                                color(Gray500)
                                property("transition", TransitionFast)
                            }
                        }) {
                            Text("\u00D7")
                        }
                    }
                }

                // "Add age range..." button (disabled in Quick mode)
                Div(attrs = {
                    if (inputsEnabled) {
                        onClick { showAgePicker = true }
                    }
                    style {
                        property("display", "inline-flex")
                        alignItems(AlignItems.Center)
                        padding(6.px, 14.px)
                        borderRadius(9999.px)
                        border(1.px, LineStyle.Dashed, if (inputsEnabled) Gray300 else Gray200)
                        fontSize(14.px)
                        color(if (inputsEnabled) Gray500 else Gray400)
                        cursor(if (inputsEnabled) "pointer" else "not-allowed")
                        property("transition", TransitionFast)
                        property("user-select", "none")
                    }
                }) {
                    Text("Add age range...")
                }
            }
        }
    }

    // Age picker modal (centered on screen)
    if (showAgePicker) {
        // Backdrop
        Div(attrs = {
            onClick { showAgePicker = false }
            style {
                property("position", "fixed")
                property("inset", "0")
                property("background", "rgba(0,0,0,0.4)")
                property("z-index", "1000")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
            }
        }) {
            // Modal card
            Div(attrs = {
                onClick { it.stopPropagation() }
                style {
                    property("background", "white")
                    borderRadius(16.px)
                    padding(24.px)
                    property("box-shadow", "0 8px 32px rgba(0,0,0,0.2)")
                    property("max-width", "280px")
                }
            }) {
                // Title
                Div(attrs = {
                    style {
                        fontSize(16.px)
                        fontWeight(600)
                        color(Color("#333"))
                        marginBottom(16.px)
                        property("text-align", "center")
                    }
                }) {
                    Text("Select age range")
                }

                // Age range grid
                Div(attrs = {
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(3, 1fr)")
                        gap(8.px)
                    }
                }) {
                    SearchCriteria.AGE_RANGES.forEach { range ->
                        val alreadySelected = range in criteria.childAgeRanges
                        Div(attrs = {
                            if (!alreadySelected) {
                                onClick {
                                    onCriteriaChange(criteria.copy(childAgeRanges = criteria.childAgeRanges + range))
                                    showAgePicker = false
                                }
                            }
                            style {
                                padding(10.px, 16.px)
                                borderRadius(8.px)
                                property("display", "inline-flex")
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                fontSize(14.px)
                                fontWeight(500)
                                if (alreadySelected) {
                                    property("background", PrimaryPale.toString())
                                    color(PrimaryLight)
                                    property("opacity", "0.4")
                                    cursor("default")
                                } else {
                                    property("background", "#f5f5f5")
                                    color(Color("#333"))
                                    cursor("pointer")
                                }
                                property("transition", TransitionFast)
                            }
                        }) {
                            Text(range)
                        }
                    }
                }
            }
        }
    }

    // Focus style for textarea
    InjectStyles(
        "search-criteria-panel-styles",
        """
        textarea:focus {
            border-color: #14A3A0 !important;
            box-shadow: 0 0 0 4px rgba(14, 124, 123, 0.1) !important;
        }
        textarea::placeholder {
            color: #A3A3A3;
        }
        """.trimIndent()
    )
}
